import { ICON_COLOR } from '@zep-us/zus/tokens';
import { useTranslation } from 'next-i18next';
import { overlay } from 'overlay-kit';
import { logger } from '@zep/core-logger';
import { learningMaterialFileModal } from '@/components/modals/LearningMaterialModal';
import { useViewport } from '@/hooks';
import { BookIcon, PaperFillIcon } from '@/icons';
import { BetaChip } from '@/shared/components/Chip/BetaChip';
import { Button } from '@/ui_v3';
import { Chip } from '@/ui_v3/components/chip/Chip';
import { useBuildFormContext } from '../form/schema';
import { type LearningMaterial, LearningMaterialUploadModal } from '../modal/LearningMaterialUploadModal';
import { SettingChangeButton } from './SettingChangeButton';
import { ToggleOptionContainer } from './ToggleOptionContainer';
export const LearningMaterialSetting = () => {
  const {
    watch,
    setValue
  } = useBuildFormContext();
  const {
    t
  } = useTranslation(['build', 'common']);
  const {
    isMobile
  } = useViewport();
  const isLearningMaterialEnabled = watch('isLearningMaterialEnabled');
  const learningMaterials = watch('materials');
  const openLearningMaterialUploadModal = async (data?: LearningMaterial) => {
    overlay.open(({
      unmount
    }) => <LearningMaterialUploadModal data={data} onClose={unmount} onSave={material => {
      logger.track('edit_learningMaterial_save', {
        fileName: material.originalFileName,
        fileSizeMB: Math.round((material.fileSize ?? 0) / 1024 ** 2),
        fileExtension: material.originalFileName?.split('.')?.pop()?.toLowerCase(),
        allowReopenDuringPlay: material.allowReopenDuringPlay
      });
      setValue('materials', [{
        ...material,
        fileSize: material.fileSize ?? 0
      }], {
        shouldDirty: true
      });
      setValue('isLearningMaterialEnabled', true);
    }} />);
  };
  const handlePreviewClick = async (material: LearningMaterial) => {
    logger.track('view_learningMaterial', {
      from: 'quizDetail'
    });
    await learningMaterialFileModal.openAsync({
      file: material,
      alwaysHasCloseButton: true
    });
  };
  return <div className="flex flex-col gap-sm" data-sentry-component="LearningMaterialSetting" data-sentry-source-file="LearningMaterialSetting.tsx">
      <ToggleOptionContainer optionIcon={<BookIcon size="sm" fill={ICON_COLOR.neutral} />} optionDescription={t('build:learningMaterial.description')} optionTitle={<span className="flex items-center gap-xs">
            {t('build:learningMaterial.title')}
            <BetaChip variant="filled" />
          </span>} isOn={isLearningMaterialEnabled} onToggle={() => {
      if (!isLearningMaterialEnabled && learningMaterials.length === 0) {
        logger.track('edit_learningMaterial_click');
        void openLearningMaterialUploadModal();
      } else {
        setValue('isLearningMaterialEnabled', !isLearningMaterialEnabled);
      }
    }} planType="FREE" isBadgeVisible={false} data-sentry-element="ToggleOptionContainer" data-sentry-source-file="LearningMaterialSetting.tsx" />
      {isLearningMaterialEnabled && <div className="flex flex-col">
          {learningMaterials.map(material => <article onClick={() => {
        if (isMobile) void handlePreviewClick(material);
      }} key={material.url} className="flex min-h-[82px] items-center gap-sm rounded-md border border-alternative px-lg py-md">
              <PaperFillIcon size="md" fill={'#0D94FF'} className="shrink-0" />
              <div className="flex min-w-0 grow flex-col gap-2xs">
                <span className="truncate text-body-md-semibold text-default" title={material.originalFileName}>
                  {material.originalFileName}
                </span>
                {material.allowReopenDuringPlay && <Chip className="w-fit border-alternative px-xs py-3xs text-caption-md-semibold" size="sm">
                    {t('common:learningMaterial.allowReopenDuringPlayChip')}
                  </Chip>}
              </div>
              <Button variant="outlined" intent="neutral" size="xs" className="hidden shrink-0 mobile-lg:flex" onClick={() => handlePreviewClick(material)}>
                {t('common:preview')}
              </Button>
              <SettingChangeButton onClick={e => {
          void openLearningMaterialUploadModal(material);
          e.stopPropagation();
        }} />
            </article>)}
        </div>}
    </div>;
};